<template>
  <div class="plan-selection-list-container">
    <h3>Plans</h3>
    <template v-if="relatedPlans.length">
      <div v-if="relatedPlans.length > 3" class="p-mb-2 p-d-flex p-jc-end">
        <Button
          :label="localSelectedPlanIntIds.length === relatedPlans.length ? 'Deselect All' : 'Select All'"
          class="p-button-text p-button-sm"
          @click="onSelectAllChange"
        />
      </div>
      <div
        v-for="(plan) of relatedPlans"
        :key="'plan_' + plan.id.intID"
      >
        <template>
          <div class="p-mb-2 p-d-flex p-ai-start">
            <Checkbox
                :id="'plan_' + plan.id.intID"
                :value="plan.id.intID"
                v-model="localSelectedPlanIntIds"
                class="p-mr-3" 
              />
            <label :for="'plan_' + plan.id.intID">
              <template v-if="plan.isNested">
                {{ plan.abbreviatedName }}  |  {{ plan.name }}
              </template>
              <template v-else>
                {{ plan.name }}
              </template>
            </label>
          </div>
        </template>
      </div>
      <Button 
        class="p-mt-6"
        label="Create Nested Variants"
        @click="createNestedVariantsInNestedPlans"
        />
    </template>
    <template v-else>
      <p>No plans available to create nested variants.</p>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ID from '@/models/ID'
import Plan from '@/models/Plan'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Tactic from '@/models/Tactic'
import Role from '@/models/Role'
import InitiativeCustomization from '@/models/InitiativeCustomization'

Vue.component('Checkbox', Checkbox)
Vue.component('Button', Button)

export default Vue.extend({
  name: 'DistributeInitiativeModal',
  props: {
    initiativeId: {
      type: ID,
      required: true,
    },
  },
  data: () => {
    return {
      localSelectedPlanIntIds: [] as number[],
    }
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    relatedPlans(): Plan[] {
      let returnArray = [] as Plan[]

      if(this.currentPlan.isLead){
        // Find the initiative
        const initiative = this.$store.getters.currentInitiatives.find((i) => i.id.intID === this.initiativeId.intID)

        // Get existing customization plan IDs for this initiative
        const existingCustomizationPlanIds = new Set(
          initiative?.initiativeCustomizations?.map((customization) => customization.planId.intID) || []
        )

        // Filter plans to only include those without existing customizations, excluding current plan and initiative's own plan
        returnArray = this.$store.getters.allPlans.filter((plan) =>
          this.currentPlan.plans.filter((id) => id.intID === plan.id.intID).length &&
          !existingCustomizationPlanIds.has(plan.id.intID) &&
          plan.id.intID !== this.currentPlan.id.intID &&
          plan.id.intID !== initiative?.planId.intID
        )
      }

      return returnArray
    },
  },
  methods: {
    createNestedVariantsInNestedPlans() {
      const initiativeToCreateVariants = this.currentPlan.initiatives.find((initiative) => initiative.id.intID === this.initiativeId.intID)
      if(initiativeToCreateVariants){
        let initiativeCustomizationsCreated = 0

        this.localSelectedPlanIntIds.forEach(async (intId) => {
          const thisPlan = this.$store.getters.allPlans.find((plan) => plan.id.intID === intId)
          const newInitiativeCustomization = new InitiativeCustomization(
            new ID(),
            this.$store.getters.currentUser.id,
            initiativeToCreateVariants.id,
            initiativeToCreateVariants.initiativeType.id,
            thisPlan.id,
            initiativeToCreateVariants.startDate,
            initiativeToCreateVariants.endDate,
            initiativeToCreateVariants.isAllDay,
          )

          try {
            await this.$store.getters.services.initiatives
              .createCustomization(newInitiativeCustomization)
              .then(
                (newInitiativeCustomizationFromServer) => {
                  this.$store.dispatch('addInitiativeCustomizationToCurrentPlan', newInitiativeCustomizationFromServer)
                  initiativeCustomizationsCreated++
                },
                (error) => {
                  Vue.prototype.$toast.add({
                    severity: AlertMessageSeverity.error,
                    summary: `Error creating variant in ${thisPlan.name}. (1)`,
                    life: 3000,
                  })
                }
              )
          } catch (error: any) {
            Vue.prototype.$toast.add({
              severity: AlertMessageSeverity.error,
              summary: `Error creating variant in ${thisPlan.name}. (2)`,
              life: 3000,
            })
          }
        })

        if(initiativeCustomizationsCreated > 0){
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.success,
            summary: 'Variant created.',
            life: 3000,
          })
        }
      }else{
        Vue.prototype.$toast.add({
          severity: AlertMessageSeverity.error,
          summary: 'Initiative not found.',
          life: 3000,
        })
      }

      this.$emit('hide')
    },
    onSelectAllChange() {
      if(this.localSelectedPlanIntIds.length === this.relatedPlans.length){
        this.localSelectedPlanIntIds = []
      }else{
        this.localSelectedPlanIntIds = this.relatedPlans.map((plan) => plan.id.intID)
      }
    }
  },
})
</script>

<style lang="scss">
.plan-selection-list-container {
  .p-checkbox-checked {
    .p-checkbox-box {
      background: #EFEFEF;
    }
    .p-checkbox-icon::before {
      content: "\e909";
      font-family: 'primeicons';
    }
  }
  .p-checkbox-disabled + label {
    opacity: 0.4;
  }
}
</style>
